/**
 * PWA install prompt (PWA_PLAN Stage 1).
 *
 * The `beforeinstallprompt` event is captured at MODULE LOAD in main.tsx
 * (it fires before React mounts — a component-mounted listener misses it;
 * that bug shipped and the card never appeared). This component reads the
 * global stash, and also listens for the custom re-broadcast for the rare
 * late fire.
 *
 * Variants:
 *  - Chromium with a captured event → real Install button
 *  - iOS Safari → Share → "Add to Home Screen" instructions
 *  - anything else (desktop Firefox/Safari, event not granted) → generic
 *    browser-menu instructions, so the affordance is never just missing
 * Hidden when already installed (standalone) or dismissed (~30 days).
 */

import { useEffect, useState } from 'react';
import { LogoMark } from '../brand/Logo';

const DISMISS_KEY = 'pwaInstallDismissedAt';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function stashedEvent(): BeforeInstallPromptEvent | null {
  return (
    ((window as unknown as { __pwaInstallEvent?: BeforeInstallPromptEvent }).__pwaInstallEvent) ??
    null
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function recentlyDismissed(): boolean {
  const at = localStorage.getItem(DISMISS_KEY);
  if (!at) return false;
  return Date.now() - Number(at) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(stashedEvent);
  const [hidden, setHidden] = useState(() => isStandalone() || recentlyDismissed());

  useEffect(() => {
    // Late fire (or fired between module load and mount): pick up the stash.
    const onAvailable = () => setInstallEvent(stashedEvent());
    window.addEventListener('pwa-install-available', onAvailable);
    return () => window.removeEventListener('pwa-install-available', onAvailable);
  }, []);

  if (hidden) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') setHidden(true);
    else dismiss();
  };

  const instructions = isIos() ? (
    <p className="text-xs text-primary-700 mt-0.5">
      Tap <span className="font-semibold">Share</span>{' '}
      <svg className="inline w-3.5 h-3.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0-12l-4 4m4-4l4 4" />
      </svg>{' '}
      then <span className="font-semibold">"Add to Home Screen"</span> to install.
    </p>
  ) : (
    <p className="text-xs text-primary-700 mt-0.5">
      Look for the <span className="font-semibold">Install</span> icon in your browser's address
      bar or menu.
    </p>
  );

  return (
    <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack p-4 flex items-start gap-3">
      {/* The mark is drawn, not fetched. This used to be an <img> pointing at
          /icons/icon-192.png, which is a stable URL the edge served as
          `immutable` for a year — so after the retheme it kept showing the old
          art. Rendering the component instead means the card can never be
          stale, and it stays sharp on any display. */}
      <span
        aria-hidden="true"
        className="w-10 h-10 shrink-0 bg-rack flex items-center justify-center"
      >
        <LogoMark size={12} on="dark" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primary-900">Install the ProgramRow app</p>
        {installEvent ? (
          <p className="text-xs text-primary-700 mt-0.5">
            Your schedule on your home screen: one tap, works offline.
          </p>
        ) : (
          instructions
        )}
        <div className="mt-2 flex gap-3">
          {installEvent && (
            <button
              type="button"
              onClick={install}
              className="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Install
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="px-2 py-1.5 text-xs text-primary-600 hover:text-primary-800"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
